"use client";

import Image from "next/image";
import { motion } from "framer-motion";
import DownloadCTA from "@/components/shared/DownloadCTA";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { cn } from "@/lib/utils";
import { CTA_DEFAULT } from "@/lib/cta-variants";

/**
 * Kompakte Split-Card im Stil von future-doctor.de: Bild links, heller
 * Cyan-Textbereich rechts, kräftiger Cyan-Button mit eigenem Ziel. Auf Mobile
 * stapelt die Karte (Bildstreifen oben, Text darunter). Feste Höhe je
 * Breakpoint, damit die Rotation der Varianten keinen Layout-Shift erzeugt.
 */
const BUTTON_CLASS =
  "inline-flex w-fit items-center rounded-md bg-[#18adcf] px-7 py-2.5 text-[15px] font-semibold " +
  "text-white transition-all duration-200 hover:bg-[#1391ad] " +
  "hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 " +
  "focus-visible:outline-offset-2 focus-visible:outline-[#18adcf]";

export default function CTASection({
  eyebrow = CTA_DEFAULT.eyebrow,
  heading = CTA_DEFAULT.heading,
  description = CTA_DEFAULT.description,
  buttonLabel = CTA_DEFAULT.buttonLabel,
  buttonHref = CTA_DEFAULT.buttonHref,
  appPopup = CTA_DEFAULT.appPopup,
  backgroundImage = CTA_DEFAULT.backgroundImage,
  className,
}: {
  eyebrow?: string;
  heading?: string;
  description?: string;
  buttonLabel?: string;
  buttonHref?: string;
  appPopup?: boolean;
  backgroundImage?: string;
  className?: string;
}) {
  return (
    <motion.aside
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={viewportOnce}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      aria-label="Weiter mit ncrechner"
      className={cn(
        "group flex flex-col overflow-hidden rounded-2xl border border-[#cfeaf3]",
        "bg-[#f2fdff] shadow-sm  sm:flex-row",
        className,
      )}
    >
      {/* Bild – oben (Mobile) bzw. links (Desktop) */}
      <div className="relative flex w-full shrink-0 overflow-hidden sm:h-auto sm:w-[38%] sm:max-w-[16rem]">
        <Image
          src={backgroundImage}
          alt=""
          fill
          sizes="(min-width: 640px) 15rem, 100vw"
          className="object-cover w-full h-full transition-transform duration-500 !relative"
        />
      </div>

      {/* Textbereich */}
      <motion.div
        variants={stagger(0.08)}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        className="flex flex-1 flex-col justify-center gap-2 p-6 sm:gap-2.5 sm:p-8"
      >
        <motion.span
          variants={fadeUp}
          className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#18adcf]"
        >
          {eyebrow}
        </motion.span>
        <motion.p
          variants={fadeUp}
          className="font-display text-xl font-bold  text-[#0b4a75] sm:text-[1.5rem] leading-[140%]"
        >
          {heading}
        </motion.p>
        <motion.p
          variants={fadeUp}
          className="text-[15px] leading-relaxed text-ink-soft sm:text-base"
        >
          {description}
        </motion.p>
        <motion.div variants={fadeUp} className="pt-1.5">
          {appPopup ? (
            <DownloadCTA
              label={buttonLabel}
              variant="accent"
              className="!min-h-0 !rounded-md !bg-[#18adcf] hover:!bg-[#1391ad] !px-7 !py-2.5 !text-[15px] !shadow-md hover:!-translate-y-0.5 !transition-all"
            />
          ) : (
            <a
              href={buttonHref}
              target="_blank"
              rel="noopener noreferrer"
              className={BUTTON_CLASS}
            >
              {buttonLabel}
            </a>
          )}
        </motion.div>
      </motion.div>
    </motion.aside>
  );
}
